/**
 * PROJECT STYLE phase 4 — FIRM STYLES (2026-10-07; plan docs/bim-plan/23-project-style.md §2, Q1).
 *
 * A STYLE is a firm-library record (kind 'style'): a complete starting look — the Windows & Doors
 * settings (frame system, muntins, setting, trim, a panel style per door condition) and a palette
 * (a firm color per role). A project STARTS FROM a style: the style's settings are COPIED into the
 * project's own openingStyle + palette, and the project records which style it came from. From
 * there the project is its own — every Project Settings edit is a customization, and "Reset to
 * style" copies the style back. A firm edit to a style is OFFERED to the projects that started from
 * it (its `updatedAt` passed the project's `appliedAt`), never forced (the firm-library rule).
 *
 * Every model mirrors the firm's styles on open (settings `firmStyles`, the firm colors
 * precedent), so the list works offline and in sandboxes. The project's own record is settings
 * `projectStyle` = { styleId, appliedAt }.
 */
import type { ModelDoc } from './doc'
import { maps, transact } from './doc'
import { normalizeOpeningStyle, type OpeningStyleSettings } from './openingStyle'
import { getPalette, PALETTE_ROLES, type PaletteRoleId } from './colors'
import { normalizeTrimStyle, type TrimStyleSettings, type EaveStyle } from './trimStyle'

export interface FirmStyleRecord {
  id: string
  name: string
  description: string
  /** ISO time of the firm's last edit — what "an update is available" compares */
  updatedAt: string
  opening: Partial<OpeningStyleSettings>
  palette: Partial<Record<PaletteRoleId, string>>
  /** TRIM & EAVES (trimStyle.ts) */
  trim?: Partial<Omit<TrimStyleSettings, 'eaves'>> & { eaves?: Partial<EaveStyle> }
  retired?: boolean
}

const FIRM_STYLES_KEY = 'firmStyles'
const PROJECT_STYLE_KEY = 'projectStyle'
const OPENING_STYLE_KEY = 'openingStyle'
const PALETTE_KEY = 'palette'
const TRIM_STYLE_KEY = 'trimStyle'

const ROLE_IDS = new Set(PALETTE_ROLES.map((r) => r.id as string))

export function normalizeFirmStyle(raw: unknown): FirmStyleRecord | null {
  const r = (raw || {}) as Partial<FirmStyleRecord>
  if (typeof r.id !== 'string' || !r.id || typeof r.name !== 'string' || !r.name.trim()) return null
  const palette: Partial<Record<PaletteRoleId, string>> = {}
  for (const [k, v] of Object.entries(r.palette || {})) if (ROLE_IDS.has(k) && typeof v === 'string' && v) palette[k as PaletteRoleId] = v
  return {
    id: r.id, name: r.name.trim(),
    description: typeof r.description === 'string' ? r.description : '',
    updatedAt: typeof r.updatedAt === 'string' && r.updatedAt ? r.updatedAt : '1970-01-01T00:00:00.000Z',
    opening: normalizeOpeningStyle(r.opening || {}),
    palette,
    trim: normalizeTrimStyle(r.trim || {}),
    ...(r.retired ? { retired: true } : {}),
  }
}

function styleMap(model: ModelDoc): Record<string, FirmStyleRecord> {
  const raw = maps(model).settings.get(FIRM_STYLES_KEY) as Record<string, unknown> | undefined
  const out: Record<string, FirmStyleRecord> = {}
  for (const [id, v] of Object.entries(raw || {})) {
    const s = normalizeFirmStyle(v)
    if (s && s.id === id) out[id] = s
  }
  return out
}

/** the firm's styles (retired ones hidden), by name */
export function listFirmStyles(model: ModelDoc): FirmStyleRecord[] {
  return Object.values(styleMap(model)).filter((s) => !s.retired).sort((a, b) => a.name.localeCompare(b.name))
}
export const getFirmStyle = (model: ModelDoc, id: string | null | undefined): FirmStyleRecord | null =>
  (id && styleMap(model)[id]) || null

/** On open: mirror the firm's styles into the model (retired kept — a project may come from one). */
export function mirrorFirmStyles(model: ModelDoc, firm: unknown[] | null | undefined): number {
  const cur = styleMap(model)
  const next: Record<string, FirmStyleRecord> = { ...cur }
  let n = 0
  for (const raw of firm || []) {
    const s = normalizeFirmStyle(raw)
    if (!s) continue
    if (JSON.stringify(cur[s.id]) === JSON.stringify(s)) continue
    next[s.id] = s
    n++
  }
  if (n) transact(model, () => { maps(model).settings.set(FIRM_STYLES_KEY, next) })
  return n
}

export interface ProjectStyleRef { styleId: string; appliedAt: string }

export function getProjectStyle(model: ModelDoc): ProjectStyleRef | null {
  const r = maps(model).settings.get(PROJECT_STYLE_KEY) as Partial<ProjectStyleRef> | undefined
  return r && typeof r.styleId === 'string' && r.styleId ? { styleId: r.styleId, appliedAt: String(r.appliedAt || '') } : null
}

/** Start from (or reset to) a firm style: its settings and palette become the project's own. */
export function applyFirmStyle(model: ModelDoc, styleId: string): boolean {
  const s = getFirmStyle(model, styleId)
  if (!s) return false
  transact(model, () => {
    // TYPICAL DOOR HEIGHTS (2026-10-08): the project's door heights and window-head choice are
    // facts about this building — a style only replaces them when it sets them itself
    const cur = normalizeOpeningStyle(maps(model).settings.get(OPENING_STYLE_KEY))
    const next = normalizeOpeningStyle(s.opening)
    maps(model).settings.set(OPENING_STYLE_KEY, {
      ...next,
      interiorDoorHeightFt: next.interiorDoorHeightFt ?? cur.interiorDoorHeightFt,
      exteriorDoorHeightFt: next.exteriorDoorHeightFt ?? cur.exteriorDoorHeightFt,
      windowHeadsAlign: next.windowHeadsAlign || cur.windowHeadsAlign,
    })
    maps(model).settings.set(PALETTE_KEY, { ...s.palette })
    maps(model).settings.set(TRIM_STYLE_KEY, normalizeTrimStyle(s.trim || {}))
    maps(model).settings.set(PROJECT_STYLE_KEY, { styleId: s.id, appliedAt: s.updatedAt })
  })
  return true
}

/** Leave the style (the settings stay as they are, just no longer tied to a firm style). */
export function clearProjectStyle(model: ModelDoc): void {
  transact(model, () => { maps(model).settings.delete(PROJECT_STYLE_KEY) })
}

/** the firm edited the project's style since the project took it */
export function styleUpdateAvailable(model: ModelDoc): boolean {
  const ref = getProjectStyle(model)
  const s = ref ? getFirmStyle(model, ref.styleId) : null
  return !!s && !s.retired && s.updatedAt > ref!.appliedAt
}

/** the project's look as a style would hold it */
export function captureProjectLook(model: ModelDoc): { opening: OpeningStyleSettings; palette: Partial<Record<PaletteRoleId, string>>; trim: TrimStyleSettings } {
  return {
    opening: normalizeOpeningStyle(maps(model).settings.get(OPENING_STYLE_KEY)),
    palette: getPalette(model),
    trim: normalizeTrimStyle(maps(model).settings.get(TRIM_STYLE_KEY)),
  }
}

/** has the project been changed from the style it started from? (which parts) */
export function styleCustomized(model: ModelDoc): { opening: boolean; palette: boolean; trim: boolean } | null {
  const ref = getProjectStyle(model)
  const s = ref ? getFirmStyle(model, ref.styleId) : null
  if (!s) return null
  const look = captureProjectLook(model)
  const palEq = (a: Record<string, string | undefined>, b: Record<string, string | undefined>) =>
    PALETTE_ROLES.every((r) => (a[r.id] || '') === (b[r.id] || ''))
  return {
    opening: JSON.stringify(normalizeOpeningStyle(s.opening)) !== JSON.stringify(look.opening),
    palette: !palEq(s.palette, look.palette),
    trim: JSON.stringify(normalizeTrimStyle(s.trim || {})) !== JSON.stringify(look.trim),
  }
}

const IN = 1 / 12

/**
 * The day-one styles (Matt, Q1 — from the firm's portfolio, in the Gayer / Lewis / McGee range).
 * The firm library is seeded from these; afterwards the firm curates them in place.
 */
export const DEFAULT_FIRM_STYLES: FirmStyleRecord[] = [
  {
    id: 'styleCalMod1', name: 'California Modern', updatedAt: '2026-10-07T00:00:00.000Z',
    description: 'Skyline, Needles. Black aluminum, no muntins, set toward the outside face, no exterior casing. Flush and pivot entries, multi-slides, flush interiors. White field, warm wood accents.',
    opening: {
      frameSystem: 'aluminum', muntinPattern: 'none', frameMount: 'exterior', exteriorTrim: 'none',
      doorConditions: {
        entry: { panelStyle: 'flush', muntinPattern: null, muntinRows: 5, muntinCols: 3 },
        patio: { panelStyle: 'fullLite', muntinPattern: null, muntinRows: 5, muntinCols: 3 },
        exteriorFrench: { panelStyle: 'fullLite', muntinPattern: null, muntinRows: 5, muntinCols: 3 },
        passage: { panelStyle: 'flush', muntinPattern: null, muntinRows: 5, muntinCols: 3 },
        interiorFrench: { panelStyle: 'fullLite', muntinPattern: null, muntinRows: 5, muntinCols: 3 },
        closet: { panelStyle: 'flush', muntinPattern: null, muntinRows: 5, muntinCols: 3 },
      },
    },
    palette: { field: 'sw7006', accent: 'mw209', trim: 'sw7006', metals: 'ral9011', winExt: 'ral9011', frontDoor: 'mw224', intWalls: 'sw7006', intTrim: 'sw7006', cabinets: 'sw7005' },
    trim: { interiorTrim: 'stoneSill', eaves: { eaveMode: 'boxed', soffitStyle: 'flat', fascia: true, gutters: true, gutterProfile: 'box' } },
  },
  {
    id: 'styleModSpan1', name: 'Modern Spanish', updatedAt: '2026-10-07T00:00:00.000Z',
    description: 'Monterey, College Park, Mulholland. Steel-look frames recessed about 4" behind a plaster return, no exterior casing. Plank entry (give it an arched head in Door Settings), divided-lite French doors, shaker interiors. White plaster, terracotta accent, black frames, stained door.',
    opening: {
      frameSystem: 'steel', muntinPattern: 'none', frameMount: 'exterior', recessFt: 4 * IN, exteriorTrim: 'none',
      doorConditions: {
        entry: { panelStyle: 'plank', muntinPattern: null, muntinRows: 5, muntinCols: 3 },
        patio: { panelStyle: 'frenchLite', muntinPattern: null, muntinRows: 5, muntinCols: 3 },
        exteriorFrench: { panelStyle: 'frenchLite', muntinPattern: null, muntinRows: 5, muntinCols: 3 },
        passage: { panelStyle: 'shaker1', muntinPattern: null, muntinRows: 5, muntinCols: 3 },
        interiorFrench: { panelStyle: 'frenchLite', muntinPattern: null, muntinRows: 5, muntinCols: 3 },
        closet: { panelStyle: 'shaker1', muntinPattern: null, muntinRows: 5, muntinCols: 3 },
      },
    },
    palette: { field: 'sw7008', accent: 'sw7701', trim: 'sw7008', metals: 'ral9005', winExt: 'ral9005', frontDoor: 'sw3511', intWalls: 'sw7008', intTrim: 'sw7008', cabinets: 'sw7005' },
    trim: { interiorTrim: 'stoneSill', eaves: { eaveMode: 'open', tails: true, tailStyle: 's3', fascia: false, gutters: true, gutterProfile: 'halfRound' } },
  },
  {
    id: 'styleModFarm1', name: 'Modern Farmhouse', updatedAt: '2026-10-07T00:00:00.000Z',
    description: 'Locust, Holly. Aluminum-clad wood, black outside and white inside, horizontal muntins, centered in the wall with a flat casing. Half-lite entry, full-lite French and patio doors, shaker interiors. White field, black frames, natural wood accent.',
    opening: {
      frameSystem: 'clad', muntinPattern: 'horizontal', muntinRows: 2, muntinCols: 1, frameMount: 'centered', exteriorTrim: 'pictureFrame',
      doorConditions: {
        entry: { panelStyle: 'halfLite', muntinPattern: null, muntinRows: 5, muntinCols: 3 },
        patio: { panelStyle: 'fullLite', muntinPattern: null, muntinRows: 5, muntinCols: 3 },
        exteriorFrench: { panelStyle: 'fullLite', muntinPattern: null, muntinRows: 5, muntinCols: 3 },
        passage: { panelStyle: 'shaker1', muntinPattern: null, muntinRows: 5, muntinCols: 3 },
        interiorFrench: { panelStyle: 'fullLite', muntinPattern: null, muntinRows: 5, muntinCols: 3 },
        closet: { panelStyle: 'shaker1', muntinPattern: null, muntinRows: 5, muntinCols: 3 },
      },
    },
    palette: { field: 'sw7006', accent: 'mw209', trim: 'sw7006', metals: 'ral9005', winExt: 'ral9005', winInt: 'sw7005', frontDoor: 'sw6258', intWalls: 'sw7005', intTrim: 'sw7005', cabinets: 'sw7005' },
    trim: { interiorTrim: 'traditional', eaves: { eaveMode: 'boxed', soffitStyle: 'sloped', fascia: true, gutters: true, gutterProfile: 'halfRound' } },
  },
  {
    id: 'styleMtnMod1', name: 'Mountain Modern', updatedAt: '2026-10-07T00:00:00.000Z',
    description: 'Walton Toll, Scenic Range. Aluminum-clad wood, bronze-black outside and natural wood inside, no muntins, centered with a wider flat casing. Plank entry and interiors, multi-slide patio. Charcoal field, stone base, natural wood trim.',
    opening: {
      frameSystem: 'clad', muntinPattern: 'none', frameMount: 'centered', exteriorTrim: 'pictureFrame',
      doorConditions: {
        entry: { panelStyle: 'plank', muntinPattern: null, muntinRows: 5, muntinCols: 3 },
        patio: { panelStyle: 'fullLite', muntinPattern: null, muntinRows: 5, muntinCols: 3 },
        exteriorFrench: { panelStyle: 'fullLite', muntinPattern: null, muntinRows: 5, muntinCols: 3 },
        passage: { panelStyle: 'plank', muntinPattern: null, muntinRows: 5, muntinCols: 3 },
        interiorFrench: { panelStyle: 'fullLite', muntinPattern: null, muntinRows: 5, muntinCols: 3 },
        closet: { panelStyle: 'flush', muntinPattern: null, muntinRows: 5, muntinCols: 3 },
      },
    },
    palette: { field: 'sw7069', accent: 'mw224', trim: 'mw209', metals: 'ral8022', winExt: 'ral8022', frontDoor: 'mw224', intWalls: 'sw7008', intTrim: 'sw7008', cabinets: 'sw7048' },
    trim: { interiorTrim: 'modern', eaves: { eaveMode: 'open', tails: true, tailStyle: 'straight', fascia: true, gutters: true, gutterProfile: 'box' } },
  },
  {
    id: 'styleOrgMod1', name: 'Organic Modern', updatedAt: '2026-10-07T00:00:00.000Z',
    description: 'Avenida, Studio Gutow, Crescent. Aluminum-clad wood, black-grey outside and natural wood inside, no muntins, toward the outside face, minimal trim. Plank entry, full-lite patio doors, flush interiors. Warm white field, charcoal accent, natural oak door.',
    opening: {
      frameSystem: 'clad', muntinPattern: 'none', frameMount: 'exterior', exteriorTrim: 'none',
      doorConditions: {
        entry: { panelStyle: 'plank', muntinPattern: null, muntinRows: 5, muntinCols: 3 },
        patio: { panelStyle: 'fullLite', muntinPattern: null, muntinRows: 5, muntinCols: 3 },
        exteriorFrench: { panelStyle: 'fullLite', muntinPattern: null, muntinRows: 5, muntinCols: 3 },
        passage: { panelStyle: 'flush', muntinPattern: null, muntinRows: 5, muntinCols: 3 },
        interiorFrench: { panelStyle: 'fullLite', muntinPattern: null, muntinRows: 5, muntinCols: 3 },
        closet: { panelStyle: 'flush', muntinPattern: null, muntinRows: 5, muntinCols: 3 },
      },
    },
    palette: { field: 'sw7551', accent: 'sw7069', trim: 'sw7551', metals: 'ral7021', winExt: 'ral7021', frontDoor: 'mw209', intWalls: 'sw7551', intTrim: 'sw7551', cabinets: 'sw7551' },
    trim: { interiorTrim: 'stoneSill', eaves: { eaveMode: 'boxed', soffitStyle: 'flat', fascia: true, gutters: true, gutterProfile: 'box' } },
  },
]
